import React from 'react';
import SubSearch from 'client/components/SubSearch/SubSearch.react';
import {LegacyFindAndApply, CheckboxChip, Button, Text} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {parseSearchString} from 'client/components/AdvancedSearch/AdvancedSearch.utils';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {Map} from 'immutable';
import {List} from 'immutable';

const TAGS_PER_PAGE = 250;

interface SearchTag {
  getId: () => string;
  getName: () => string;
}

interface SubSearchResultsArgs {
  onCheck: (e: React.ChangeEvent<HTMLInputElement>, result: SearchTag) => void;
  results: List<SearchTag>;
  selectedItems: Map<string, SearchTag>;
  searchString: string;
}

interface Props {
  subjectId: string;
  guideType: string;
  defaultSelectedItems?: Map<string, SearchTag>;
  onApply?: (selectedItems: Map<string, SearchTag>) => void;
  onCancel?: () => void;
}

interface Paging {
  searchString: string;
  additionalResults: List<SearchTag>;
  currentPage: number;
  isLoadingMore: boolean;
  loadError: string | null;
}

interface State {
  paging: Paging;
}

const makeEmptyPaging = (searchString: string): Paging => ({
  searchString,
  additionalResults: List(),
  currentPage: 1,
  isLoadingMore: false,
  loadError: null
});

const getTotalPages = (query: {getResourceMetadata: () => unknown}): number | undefined =>
  (query.getResourceMetadata() as Map<string, unknown> | undefined)?.getIn([
    'page',
    'total_pages'
  ]) as number | undefined;

export default class TagsSubSearch extends React.Component<Props, State> {
  state: State = {paging: makeEmptyPaging('')};

  getPaging = (searchString: string): Paging =>
    this.state.paging.searchString === searchString
      ? this.state.paging
      : makeEmptyPaging(searchString);

  makeQuery = (searchString: string, page = 1) => {
    const {keywords, phrases, isSearchingForKeywords, isSearchingForPhrases} =
      parseSearchString(searchString);

    return resource('search_tags_v1')
      .sort('name')
      .mandarkEndpoint(['search_tags_v1'])
      .filter({
        question_sets_v1: {
          subject_v2: {
            id: this.props.subjectId
          },
          guide_levels_v2: {
            nlevel: 1,
            guide_v1: {
              guide_type: this.props.guideType
            }
          },
          questions_v3: {
            status: 'published' // Only include published questions
          }
        }
      })
      .filter(
        {
          document_stems: {
            search_tokens: encodeURIComponent(keywords)
          }
        },
        isSearchingForKeywords
      )
      .filter(
        {
          document: {
            search_phrases: encodeURIComponent(String(phrases))
          }
        },
        isSearchingForPhrases
      )
      .pageSize(TAGS_PER_PAGE)
      .page(page);
  };

  handleLoadMore = async (searchString: string) => {
    const paging = this.getPaging(searchString);
    this.setState({paging: {...paging, isLoadingMore: true, loadError: null}});

    const nextPage = paging.currentPage + 1;
    const query = this.makeQuery(searchString, nextPage);

    try {
      const newResults = (await query.getResourcePromise()) as List<SearchTag>;

      this.setState((prevState) =>
        prevState.paging.searchString === searchString
          ? {
              paging: {
                ...prevState.paging,
                additionalResults: prevState.paging.additionalResults.concat(
                  newResults
                ) as List<SearchTag>,
                currentPage: nextPage,
                isLoadingMore: false
              }
            }
          : null
      );
    } catch (error) {
      console.error('Error loading more tags:', error);
      query.invalidateInterest();
      this.setState((prevState) =>
        prevState.paging.searchString === searchString
          ? {
              paging: {
                ...prevState.paging,
                isLoadingMore: false,
                loadError: 'Failed to load more tags. Please try again.'
              }
            }
          : null
      );
    }
  };

  render() {
    return (
      <SubSearch
        {...this.props}
        className='advanced-search__sub-search advanced-search__sub-search--tags'
        queryFunc={(searchString) => this.makeQuery(searchString, 1)}
        allowEmptyQuery
        renderResultsFunc={({
          onCheck,
          results,
          selectedItems,
          searchString
        }: SubSearchResultsArgs) => {
          const paging = this.getPaging(searchString);
          const allResults = results.concat(paging.additionalResults);
          const totalPages = getTotalPages(this.makeQuery(searchString, 1));
          const hasMore = totalPages !== undefined && paging.currentPage < totalPages;

          return (
            <LegacyFindAndApply.Body className='advanced-search__sub-search__results'>
              {allResults.map((result?: SearchTag) => (
                <CheckboxChip
                  data-id={result!.getId()}
                  icon='tag'
                  key={result!.getId()}
                  onChange={(e) => onCheck(e, result!)}
                  checked={selectedItems.has(result!.getId())}
                >
                  <MarkdownRendererV2 text={result!.getName()} />
                </CheckboxChip>
              ))}
              {paging.loadError && (
                <div className='p-x2 text-center'>
                  <Text color='negative' size='s'>
                    {paging.loadError}
                  </Text>
                </div>
              )}
              {hasMore && (
                <div className='p-x2'>
                  <Button
                    variant='outlined'
                    onClick={() => this.handleLoadMore(searchString)}
                    disabled={paging.isLoadingMore}
                    className='w-full'
                  >
                    {paging.isLoadingMore ? 'Loading...' : 'Load more tags'}
                  </Button>
                </div>
              )}
            </LegacyFindAndApply.Body>
          );
        }}
      />
    );
  }
}
